import { Pause, Play } from "@phosphor-icons/react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";

import { PlaybackWaveform } from "./PlaybackWaveform";
import TimerDisplay from "./TimerDisplay";

function pauseAllOtherAudios(currentAudio: HTMLAudioElement) {
  const allAudios = document.querySelectorAll("audio");
  for (let i = 0; i < allAudios.length; i += 1) {
    const audioElement = allAudios[i];
    if (audioElement !== currentAudio && !audioElement.paused) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
  }
}

interface AudioPlayerProps {
  audioBlob: Blob;
  initialPlaybackTime?: number;
  isLoading?: boolean;
}

export default function AudioPlayer({
  audioBlob,
  initialPlaybackTime,
  isLoading = false,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const playbackPauseTimeRef = useRef<number>(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(initialPlaybackTime || 0);
  const [rawAudioData, setRawAudioData] = useState<Float32Array>();
  const [isAudioReady, setIsAudioReady] = useState(false);

  const audioUrl = useMemo(() => {
    if (audioBlob) {
      const modifiedBlob = audioBlob.type.startsWith("audio/mp4")
        ? audioBlob
        : new Blob([audioBlob], { type: "audio/mp4" });
      return URL.createObjectURL(modifiedBlob);
    }
  }, [audioBlob]);

  useLayoutEffect(() => {
    setIsPlaying(false);

    if (audioBlob) {
      setIsAudioReady(false);

      if (initialPlaybackTime) {
        setPlaybackTime(initialPlaybackTime);
      }
      audioBlob.arrayBuffer().then((arrayBuffer) => {
        const audioCtx = new (
          window.AudioContext || window.webkitAudioContext
        )();

        audioCtx.decodeAudioData(arrayBuffer).then((audioBuffer) => {
          const rawData = audioBuffer.getChannelData(0);

          setRawAudioData(rawData);
          playbackPauseTimeRef.current = 0;

          if (!initialPlaybackTime) {
            setPlaybackTime(Math.round(audioBuffer.duration));
          }
          audioCtx.close();
        });
      });
    } else {
      setPlaybackTime(0);
    }
  }, [audioBlob]);

  function handlePlay() {
    if (audioRef.current) {
      pauseAllOtherAudios(audioRef.current);
      audioRef.current.play();
    }
  }
  function handlePause() {
    if (audioRef.current) {
      audioRef.current.pause();
      playbackPauseTimeRef.current = performance.now();
    }
  }

  // Function (not a value) so PlaybackWaveform can read it in its rAF loop for
  // smooth 60fps progress, instead of stuttery React re-renders via onTimeUpdate.
  const getPlayedRatio = () => {
    const audio = audioRef.current;
    if (!audio?.duration || audio.ended) return 0;

    // Ignore codec pre-roll: only show progress if actively playing
    // or if user explicitly paused mid-playback
    if (audio.paused && playbackPauseTimeRef.current === 0) return 0;

    return Math.min(1, audio.currentTime / audio.duration);
  };

  const showLoading =
    isLoading || (!!audioBlob && !isAudioReady && !rawAudioData);

  return (
    <div className="flex w-full flex-row items-center justify-between gap-x-2 rounded-full lg:justify-center">
      <Button
        size="sm"
        intent="iconOnly"
        variant="default-purple"
        onClick={isPlaying ? handlePause : handlePlay}
        aria-label={isPlaying ? "pause playback" : "play recording"}
        isLoading={showLoading}
      >
        {isPlaying ? <Pause weight="fill" /> : <Play weight="fill" />}
      </Button>

      {showLoading ? (
        <Skeleton className="h-2 flex-1 lg:w-54 w-47.5" />
      ) : (
        <PlaybackWaveform
          audioData={rawAudioData}
          getPlayedRatio={getPlayedRatio}
        />
      )}

      <TimerDisplay
        className={isPlaying ? "text-purple-600" : "text-black-500"}
        value={playbackTime}
      />

      {/* Audio element for playback */}
      <audio
        ref={audioRef}
        src={audioUrl}
        className="sr-only"
        onTimeUpdate={({ currentTarget: { currentTime } }) => {
          // this prevents time update on mount (before playback started)
          if (isPlaying) {
            setPlaybackTime(Math.round(currentTime));
          }
        }}
        onPlay={() => {
          setIsPlaying(true);

          // this sets the playback time to 0 immediately on first playback start
          // for better UXwe don't do this on pause
          if (playbackPauseTimeRef.current === 0) {
            setPlaybackTime(0);
          }
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onEnded={() => {
          playbackPauseTimeRef.current = 0;
        }}
        onCanPlayThrough={() => {
          setIsAudioReady(true);
        }}
      >
        <track kind="captions" />
      </audio>
    </div>
  );
}
